import React, { useState } from "react";
import { endPoints } from "../../constant/Environment";
import { addData } from "../../Utility/API";
import { Button } from "react-bootstrap";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";

/** The API sends draw time as an ISO instant (e.g. "2026-07-29T18:30:00-04:00");
 * the datetime-local picker needs the browser's local "YYYY-MM-DDTHH:mm" instead. */
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const CreateLottery = (props) => {
  const { onClose, editData, open, ...other } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [multiplier, setMultiplier] = useState("");
  const [ticketPrice, setTicketPrice] = useState("");
  const [totalTickets, setTotalTickets] = useState("");
  const [maxNumber, setMaxNumber] = useState("30");
  const [ballCount, setBallCount] = useState("3");
  const [drawTime, setDrawTime] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (open) {
      setErrors({});
      if (Object.keys(editData).length === 0) {
        setName("");
        setMultiplier("");
        setTicketPrice("");
        setTotalTickets("");
        setMaxNumber("30");
        setBallCount("3");
        setDrawTime("");
        setIsActive(false);
      } else {
        setName(editData?.name || "");
        setMultiplier(editData?.multiplier ?? "");
        setTicketPrice(editData?.ticketPrice ?? "");
        setTotalTickets(
          editData?.totalTickets > 0 ? String(editData.totalTickets) : ""
        );
        setMaxNumber(editData?.maxNumber ?? "30");
        setBallCount(editData?.ballCount ? String(editData.ballCount) : "3");
        setDrawTime(toPickerValue(editData?.drawTime));
        setIsActive(editData?.isActive === 1);
      }
    }
  }, [editData, open]);

  const previewPrize =
    ticketPrice !== "" && multiplier !== ""
      ? (parseFloat(ticketPrice) * parseFloat(multiplier)).toFixed(2)
      : null;

  const clearError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const validate = () => {
    const next = {};

    if (!name.trim()) {
      next.name = "Lottery name is required";
    }

    const multiplierNum = parseFloat(multiplier);
    if (multiplier === "" || isNaN(multiplierNum) || multiplierNum <= 0) {
      next.multiplier = "Enter a multiplier greater than 0";
    }

    const ticketPriceNum = parseFloat(ticketPrice);
    if (ticketPrice === "" || isNaN(ticketPriceNum) || ticketPriceNum <= 0) {
      next.ticketPrice = "Enter a ticket price greater than 0";
    }

    // Blank or 0 means unlimited tickets, so only validate when a value was entered.
    if (totalTickets !== "") {
      const totalTicketsNum = parseInt(totalTickets, 10);
      if (isNaN(totalTicketsNum) || totalTicketsNum < 0) {
        next.totalTickets = "Enter a whole number, or leave blank for unlimited";
      }
    }

    const maxNumberNum = parseInt(maxNumber, 10);
    if (maxNumber === "" || isNaN(maxNumberNum) || maxNumberNum <= 0) {
      next.maxNumber = "Enter a whole number greater than 0";
    }

    const ballCountNum = parseInt(ballCount, 10);
    if (ballCount === "" || isNaN(ballCountNum) || ballCountNum <= 2) {
      next.ballCount = "Must be greater than 2";
    } else if (!isNaN(maxNumberNum) && maxNumberNum > 0 && ballCountNum > maxNumberNum) {
      next.ballCount = `Cannot exceed the ball range (1-${maxNumberNum})`;
    }

    if (!drawTime) {
      next.drawTime = "Draw time is required";
    } else if (new Date(drawTime).getTime() <= Date.now()) {
      next.drawTime = "Draw time must be in the future";
    }

    return next;
  };

  const handleCancel = () => {
    onClose();
  };

  const handleOk = () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      ErrorToast("Please fill the required fields");
      return;
    }

    let url = editData?.id
      ? `${endPoints.api.UPDATE_LOTTERY}`
      : `${endPoints.api.CREATE_LOTTERY}`;

    let payload = {
      name: name.trim(),
      multiplier: parseFloat(multiplier),
      ticketPrice: parseFloat(ticketPrice),
      // Blank field means unlimited tickets; the backend treats totalTickets <= 0 as unlimited.
      totalTickets: totalTickets === "" ? 0 : parseInt(totalTickets, 10),
      maxNumber: parseInt(maxNumber, 10),
      ballCount: parseInt(ballCount, 10),
      drawTime: new Date(drawTime).toISOString(),
      isActive: isActive ? 1 : 0,
    };

    if (editData?.id) {
      payload.id = editData.id;
    }

    setIsLoading(true);
    addData(url, payload)
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.status === "success") {
          SuccesToast(response?.data?.message);
          onClose();
        } else {
          ErrorToast(response?.data?.message || "Unable to save lottery.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast("Unable to save lottery.");
      });
  };

  return (
    <Dialog maxWidth="sm" open={open} {...other}>
      <DialogTitle>Lottery Info</DialogTitle>
      <DialogContent dividers>
        <Toaster></Toaster>
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Lottery Name"
            value={name}
            error={!!errors.name}
            helperText={errors.name}
            onChange={(event) => {
              setName(event.target.value);
              clearError("name");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Ticket Price ($)"
            type="number"
            value={ticketPrice}
            error={!!errors.ticketPrice}
            helperText={errors.ticketPrice}
            onChange={(event) => {
              setTicketPrice(event.target.value);
              clearError("ticketPrice");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Prize Multiplier (e.g. 10 = 10x ticket price)"
            type="number"
            value={multiplier}
            error={!!errors.multiplier}
            helperText={errors.multiplier}
            onChange={(event) => {
              setMultiplier(event.target.value);
              clearError("multiplier");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Total Tickets"
            type="number"
            value={totalTickets}
            error={!!errors.totalTickets}
            helperText={errors.totalTickets || "Leave blank or 0 for unlimited tickets"}
            onChange={(event) => {
              setTotalTickets(event.target.value);
              clearError("totalTickets");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Ball Number Range (1 to N)"
            type="number"
            value={maxNumber}
            error={!!errors.maxNumber}
            helperText={errors.maxNumber}
            onChange={(event) => {
              setMaxNumber(event.target.value);
              clearError("maxNumber");
              clearError("ballCount");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <TextField
            variant="outlined"
            label="Numbers to Pick (must be greater than 2)"
            type="number"
            inputProps={{ min: 3 }}
            value={ballCount}
            error={!!errors.ballCount}
            helperText={errors.ballCount}
            onChange={(event) => {
              setBallCount(event.target.value);
              clearError("ballCount");
            }}
          />
        </FormControl>

        <FormControl style={{ width: "100%", marginTop: "20px" }}>
          <label>Draw Time</label>
          <TextField
            variant="outlined"
            type="datetime-local"
            value={drawTime}
            error={!!errors.drawTime}
            helperText={errors.drawTime}
            onChange={(event) => {
              setDrawTime(event.target.value);
              clearError("drawTime");
            }}
          />
        </FormControl>

        {previewPrize && (
          <div style={{ marginTop: "12px", fontSize: "13px", color: "#03B349", fontWeight: 600 }}>
            Winner takes: ${ticketPrice} × {multiplier} = {previewPrize}
          </div>
        )}

        <FormControlLabel
          control={
            <Switch
              name="active"
              checked={isActive}
              onClick={() => setIsActive(!isActive)}
            />
          }
          labelPlacement="start"
          label="Active"
        />
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel}>
          Cancel
        </Button>
        <Button onClick={handleOk}>Ok</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLottery;
